/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_QUERY,
  EVALUATION_SORT_FIELDS,
  MAX_SEARCH_LENGTH,
  PAGE_SIZES,
  THREAT_LEVELS,
  type EvaluationSortField,
  type EvaluationsQuery,
  type ThreatLevel,
  type ViolationsFilter,
} from './evaluations.types';

type UrlParams = Record<string, string | undefined | null>;

/** Each value is percent-encoded before joining, so a name that contains a comma survives. */
const decode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const list = (value: string | undefined | null): string[] =>
  value ? value.split(',').filter(Boolean).map(decode) : [];

const join = (values: string[]): string | undefined => values.map(encodeURIComponent).join(',') || undefined;

const positive = (value: string | undefined | null, fallback: number): number => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

const isThreatLevel = (value: string): value is ThreatLevel => (THREAT_LEVELS as readonly string[]).includes(value);

const isSortField = (value: string | undefined | null): value is EvaluationSortField =>
  !!value && (EVALUATION_SORT_FIELDS as readonly string[]).includes(value);

const isPageSize = (value: number): boolean => (PAGE_SIZES as readonly number[]).includes(value);

const violations = (value: string | undefined | null): ViolationsFilter =>
  value === 'with' || value === 'without' ? value : null;

export function parseEvaluationsUrl(params: UrlParams): EvaluationsQuery {
  const pageSize = positive(params.pageSize, DEFAULT_PAGE_SIZE);
  return {
    formats: list(params.format),
    threatLevels: list(params.threat).filter(isThreatLevel),
    violations: violations(params.violations),
    search: (params.q ?? '').slice(0, MAX_SEARCH_LENGTH),
    sortBy: isSortField(params.sort) ? params.sort : DEFAULT_QUERY.sortBy,
    asc: params.dir !== 'desc',
    page: positive(params.page, 1),
    pageSize: isPageSize(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
  };
}

/** Defaults are dropped so the URL stays short. */
export function serializeEvaluationsUrl(query: EvaluationsQuery): Record<string, string | undefined> {
  return {
    format: join(query.formats),
    threat: join(query.threatLevels),
    violations: query.violations ?? undefined,
    q: query.search || undefined,
    sort: query.sortBy !== DEFAULT_QUERY.sortBy ? query.sortBy : undefined,
    dir: query.asc ? undefined : 'desc',
    page: query.page > 1 ? String(query.page) : undefined,
    pageSize: query.pageSize !== DEFAULT_PAGE_SIZE ? String(query.pageSize) : undefined,
  };
}
